import UserInfoFactory from './user-info';
const ApiBase = require('./api');
const CompanyInstance = require('./company').instance;
const CompanyFactory = require('./company').factory;
const DesygnerCompanyFactory = require('./desygner-company');
const DesygnerApi = require('./desygner-api');
const EditorFacade = require('../facade/editor/editor');
const DesignFacade = require('../facade/design/design');

function FormatFactory () {
  this.resource = 'management/formatgroups';
  this.apiInit = _.memoize(ApiBase.prototype.init);
  this.externalTemplates = [{
    name: 'Wattpad',
    domain: 'wattpad-master',
    flavour: 'watt'
  }];
}

FormatFactory.prototype = new ApiBase();

FormatFactory.prototype.init = function () {
  this.useWeBrandFormats = INKIVE.Helpers.isWeBrandProject();
  this.parentCompany = INKIVE.C.WHITE_LABEL_CONFIGURATION &&
    INKIVE.C.WHITE_LABEL_CONFIGURATION.parent_company;

  if (!this.useWeBrandFormats) {
    return this.apiInit();
  }

  if (!this.initDeferred) {
    this.initDeferred = new Promise(resolve => {
      EditorFacade.on('editor.loaded', addInlineSize.bind(this));
      EditorFacade.on('page.move_to', addInlineSize.bind(this));

      Promise.all([
        UserInfoFactory.get(),
        getCompanyData()
      ]).then(async ([userData, companyData]) => {
        const flavours = userData.flavours || [];
        const externalTemplates = this.externalTemplates
          .filter(template => flavours.includes(template.flavour))
        ;

        this.companyData = companyData || [];

        for (const external of externalTemplates) {
          const externalDomain = external.domain;
          const externalData = await getCompanyData(externalDomain);

          if (externalData && externalData.length) {
            this.companyData = [
              ...this.companyData,
              ...externalData.map(campaign => {
                campaign.external = external;
                campaign.data.forEach(format => {
                  format.external = externalDomain;
                });
                return campaign;
              })
            ];
          }
        }

        if (this.parentCompany) {
          const parentCompanyData = await getCompanyData(this.parentCompany);

          if (parentCompanyData && parentCompanyData.length) {
            this.companyData = [...this.companyData, ...parentCompanyData];
          }
        }

        resolve(this.companyData);
      });
    });
  }

  return this.initDeferred;

  function getCompanyData (external) {
    const workspace = external || 'desygner';
    const companyInstance = external ? new CompanyFactory(external) : CompanyInstance;
    const campaignPromise = companyInstance.get('campaigns?consume');
    const formatsPromise = companyInstance.get('formats?consume');
    const foldersPromise = DesygnerApi.get(`brand/companies/${workspace}/campaigns-folders/root/children`);

    return new Promise(resolve => {
      Promise
        .all([campaignPromise, formatsPromise, foldersPromise])
        .then(([campaigns, formats, folders]) => {
          resolve(mapData(campaigns, formats, folders));
        })
      ;
    });
  }

  function mapData (campaigns, formats, folders) {
    if (campaigns && formats) {
      return campaigns
        .map(campaign => {
          if (_.isEmpty(folders)) {
            folders = [];
          }

          const folder = folders.find(folder => folder.id === campaign.folder) || {};
          const campaignFormats = formats
            .filter(format => format.campaign === campaign.id)
            .map(format => {
              return {
                ...format,
                string_id: 'f_' + format.id
              };
            })
          ;

          return {
            key: 'c_' + campaign.id,
            name: campaign.name,
            data: campaignFormats,
            folderId: folder.id,
            folderName: folder.name
          };
        })
        .sort((a, b) => a.folderId > b.folderId ? 1 : -1);
    }

    return [];
  }
};

FormatFactory.prototype.getGroup = function (key) {
  const deferred = $.Deferred();

  this.getGroups([key]).then(function (data) {
    deferred.resolve(data[0]);
  }, function (err) {
    deferred.reject(err + ' | Formats GetGroup error');
  });

  return deferred.promise();
};

FormatFactory.prototype.getGroups = function (keys) {
  const deferred = $.Deferred();
  const toInt = (k) => parseInt(k.match(/\d/g).join(''), 10);

  this.init().then((data) => {
    if (!keys || !keys.length) {
      deferred.resolve(data);
    } else {
      const keysAsInt = keys.map((key) => toInt(key));
      const groups = data.filter(({ key }) => keysAsInt.indexOf(toInt(key)) !== -1) || [];
      deferred.resolve(groups);
    }
  }, (err) => {
    deferred.reject(err + ' | Formats GetGroups error');
  });

  return deferred.promise();
};

FormatFactory.prototype.getFormat = function (key) {
  const deferred = $.Deferred();

  this.getFormats([key]).then(function (data) {
    deferred.resolve(data[0]);
  }, function (err) {
    deferred.reject(err + ' | Formats GetFormat error');
  });

  return deferred.promise();
};

FormatFactory.prototype.getFormats = function (keys) {
  const deferred = $.Deferred();

  this.init().then(function (data) {
    if (INKIVE.C.IS_MOBILE_APP) {
      data = data.groups;
    }

    const formats = _.flatten(_.map(data, function (group) {
      return group.data;
    }));

    if (!keys) deferred.resolve(formats);
    else {
      deferred.resolve(_.filter(formats, function (format) {
        return _.any(keys, function (key) {
          key = parseInt(key);
          return key === parseInt(format.string_id) || key === parseInt(format.id);
        });
      }));
    }
  }, function (err) {
    deferred.reject(err + ' | Formats GetFormats error');
  });

  return deferred.promise();
};

FormatFactory.prototype.getFormatTemplates = function (format) {
  return new Promise(resolve => {
    this.init().then(data => {
      CompanyInstance
        .get(['formats', format.id, 'templates'])
        .then(templates => resolve(templates))
      ;
    });
  });
};

FormatFactory.prototype.getFormatById = function (id) {
  return new Promise(resolve => {
    this.init().then(data => {
      DesygnerCompanyFactory
        .get(['formats', id])
        .then(format => resolve(format), () => {
          resolve({});
        })
      ;
    });
  });
};

FormatFactory.prototype.getFormatSiblings = function (format) {
  return new Promise(resolve => {
    this.init().then(data => {
      let formatId;
      if (format.id) {
        formatId = format.id;
      } else {
        formatId = format.parent_format;
      }

      if (!this.parentCompany) {
        resolve([]);
        return;
      }

      const parent = new CompanyFactory(this.parentCompany);

      parent
        .get(['formats', formatId, 'templates?siblings'])
        .then(templates => resolve(templates), () => {
          resolve([]);
        })
      ;
    });
  });
};

FormatFactory.prototype.getFormatsBasedOnSize = function (size) {
  const deferred = $.Deferred();

  this.getFormats().then(_.bind(function (data) {
    const sameSize = _.bind(this.isSameSize, this, size);
    deferred.resolve(_.filter(data, sameSize));
  }, this), function (err) {
    deferred.reject(err + ' | Formats GetFormatsBasedOnSize error');
  });

  return deferred.promise();
};

FormatFactory.prototype.getCurrentSize = function () {
  const tool = INKIVE.Tool.getInstance();
  const canvas = tool.editor.canvas;
  const size = {
    width: canvas.contentW,
    height: canvas.contentH
  };
  const inlinedSize = INKIVE.Helpers.getInlineSize();
  const validUnits = ['px', 'in', 'cm', 'mm'];

  if (inlinedSize.unit && _.contains(validUnits, inlinedSize.unit)) {
    return inlinedSize;
  }

  return size;
};

FormatFactory.prototype.isSameSize = function (size, format) {
  const convertToPixels = INKIVE.Helpers.convertUnitsToPixels;
  const threshold = 10; // in pixels

  if (size.unit) {
    return size.unit === format.unit &&
      size.width === format.width &&
      size.height === format.height;
  }

  let w = convertToPixels(format.width, format.unit, 1, 96);
  let h = convertToPixels(format.height, format.unit, 1, 96);

  let isSame = Math.abs(size.width - w) < threshold && Math.abs(size.height - h) < threshold;

  if (!isSame) {
    w = convertToPixels(format.width, format.unit, 1, 72);
    h = convertToPixels(format.height, format.unit, 1, 72);

    isSame = Math.abs(size.width - w) < threshold && Math.abs(size.height - h) < threshold;
  }

  return isSame;
};

FormatFactory.prototype.getCurrentFormat = function () {
  const deferred = $.Deferred();
  const filterId = getInlineFormatId() || INKIVE.Helpers.getFragmentParam('filter');
  const size = this.getCurrentSize();

  const getFormatsBasedOnSize = _.bind(function (fallbackFormat) {
    this.getFormatsBasedOnSize(size).then(function (formats) {
      deferred.resolve(formats.length ? formats[0] : fallbackFormat);
    });
  }, this);

  const setPrintingGuidelines = (format) => {
    const metadata = format.root_format_metadata || format.metadata;

    INKIVE.C.PRINTING_TYPE = metadata?.print_properties;
  };

  if (filterId) {
    $.when(this.getFormat(filterId), this.getFormatsBasedOnSize(size))
      .then(_.bind(function (format, formats) {
        if (format && this.isSameSize(size, format)) {
          deferred.resolve(format);
        } else {
          deferred.resolve(formats[0]);
        }
      }, this));
  } else if (INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE) {
    CompanyInstance.get(['templates', INKIVE.C.BOOK.encodedId]).then(function (template) {
      CompanyInstance.get(['formats', template.format]).then(function (format) {
        setPrintingGuidelines(format);
        deferred.resolve(format);
      });
    });
  } else if (INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.DESYGNER_DESIGN) {
    CompanyInstance.get(['designs', INKIVE.C.BOOK.encodedId]).then((template) => {
      const isSameFormat = template.format && this.isSameSize(size, template.format);

      setPrintingGuidelines(template.format);
      isSameFormat
        ? deferred.resolve(template.format)
        : getFormatsBasedOnSize(template.format);
    }, getFormatsBasedOnSize);
  } else if (INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.RESTRICTED_DESIGN) {
    CompanyInstance.get(['designs', INKIVE.C.BOOK.encodedId]).then((template) => {
      setPrintingGuidelines(template.format);
      deferred.resolve(template.format);
    }, getFormatsBasedOnSize);
  } else {
    getFormatsBasedOnSize();
  }
  return deferred.promise();
};

FormatFactory.prototype.getPrintableFormats = function () {
  const deferred = $.Deferred();

  this.getGroups().then((groups) => {
    const printableFormats = _.flatten(groups.map((d) => {
      return _.where(d.data, { is_printable: true });
    }));

    deferred.resolve(printableFormats);
  }, (error) => {
    deferred.reject(error + ' | Formats getPrintableFormats error');
  });

  return deferred.promise();
};

function getInlineFormatId () {
  let className = $('#svgcontent > g.main').attr('class');
  if (!className) return null;

  className = _.find(className.split(' '), function (className) {
    return className.match(/format-id-[\d+]/);
  });

  return className ? className.split('-')[2] : null;
}

function addInlineSize () {
  !INKIVE.Helpers.isInlineSizeValid() && this.getCurrentFormat().then((format) => {
    // After getCurrentFormat, INKIVE.Helpers.getInlineSize() is not empty anymore due to multiple 'editor.loaded' events
    // Very NB! Prevents adding dimension class for formats matching the initial canvas size of 737x521px during editor load
    if (INKIVE.Helpers.isInlineSizeValid()) return false;

    INKIVE.Helpers.removeClassDimensions();
    const svgContent = document.getElementById('svgcontent');
    const svgContentClassList = svgContent.classList && svgContent.classList.value;

    // Only add class info if there isn't one already existing, preventing duplicates and mismatch errors
    if (!svgContentClassList.match(/du-(\w+)/g) &&
      !svgContentClassList.match(/dw-([\d|\.]+)/g) &&
      !svgContentClassList.match(/dh-([\d|\.]+)/g)
    ) {
      svgContent.classList.add('dw-' + format.width);
      svgContent.classList.add('dh-' + format.height);
      svgContent.classList.add('du-' + format.unit);
    }

    DesignFacade.trigger('template.update.size');

    return true;
  });
}

module.exports = new FormatFactory();
